import { APP_BASE_HREF, CommonModule, DOCUMENT, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NgZone,
  signal,
  viewChild
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth';
import { PageLayoutSettingsService } from '@services/page-layout-settings.service';
import { AlertContainerComponent, LoaderComponent, ModalService, SafePipe, SidepanelService, Toast, ToastService } from 'mbs-ui-kit';
import { EmptyPageService } from './empty-page.service';
import { InteractionEvent } from './InteractionEvent';

@Component({
  selector: 'mbs-empty-page',
  templateUrl: './empty-page.component.html',
  styleUrls: ['./empty-page.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterOutlet, LoaderComponent, AlertContainerComponent, SafePipe],
  providers: [EmptyPageService, SidepanelService, ModalService, { provide: APP_BASE_HREF, useValue: environment.baseHref || '/' }],
  host: {
    '(window:message)': 'handleMessage($event)',
    '(window:blur)': 'handleWindowBlur()',
    class: 'd-flex flex-column flex-fill h-100'
  }
})
export class EmptyPageComponent {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private router = inject(Router);
  private auth = inject(AuthService);
  private toastService = inject(ToastService);
  private emptyPageService = inject(EmptyPageService);
  private pageLayoutSettings = inject(PageLayoutSettingsService);
  private appBaseHref = inject(APP_BASE_HREF);
  private dom = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);
  private hostEl = inject(ElementRef);

  private readonly ASPX_PAGE_POSTFIX = '.aspx';
  private iframeRef = viewChild<ElementRef<HTMLIFrameElement>>('iframeRef');
  private iframeWindow = computed<Window>(() => this.iframeRef().nativeElement.contentWindow);
  private fullBaseHref = signal<string>('');

  protected iframeSrc = signal<string>('');
  protected loading = signal<boolean>(true);

  constructor() {
    effect(() => {
      this.iframeWindow() && this.emptyPageService.init(this.iframeWindow());
    });

    /*
    // Temporarily disabled until the backend API becomes available.
    effect(() => {
      const authenticated = this.auth.isAuthenticated();
      const userEmail = this.auth.userEmail();

      if (this.lastAuthStatus === authenticated) {
        return;
      }

      this.lastAuthStatus = authenticated;

      if (!authenticated) {
        this.toastService.info('User logged out');
        return;
      }

      const email = userEmail ? ` with email ${userEmail}` : '';
      const text = `Authenticated${email}`;

      this.toastService.success(text);
    });
    */

    this.destroyRef.onDestroy(() => {
      this.emptyPageService.destroy();
    });

    this.fullBaseHref.set((environment.baseHref || window.location.origin) + this.appBaseHref);

    const ref = this.route.snapshot.queryParams.ref;
    if (ref) {
      this.auth.rememberReturnUrl(ref);
    }

    this.setIframeSrc(window.location.href);
  }

  setIframeSrc(url: string): void {
    const currentUrl = new URL(url);
    currentUrl.pathname = currentUrl.pathname.replace('/AP/', '/Admin/');

    if (!currentUrl.pathname.includes(this.ASPX_PAGE_POSTFIX)) {
      currentUrl.pathname += this.ASPX_PAGE_POSTFIX;
    }

    this.iframeSrc.set(this.appendIframeParameter(currentUrl.href));
    this.updateHeaderFooterVisibility(currentUrl.pathname);
    if (this.iframeWindow) {
      setTimeout(() => {
        try {
          this.iframeWindow() && this.iframeWindow().location.assign(this.iframeSrc());
        } catch (error) {
          this.iframeRef().nativeElement.src = this.iframeSrc();
        }
      });
    }
  }

  reloadIframe(): void {
    this.iframeWindow() && this.iframeWindow().location.reload();
  }

  appendIframeParameter(rawUrl: string): string {
    const url = new URL(rawUrl);
    const search = url.searchParams;
    // search.set('IFRAME', 'true');

    return this.removeDoubleSlash(this.fullBaseHref() + url.href.replace(url.origin, ''));
  }

  removeDoubleSlash(url: string): string {
    const removeDoubleSlash = /(:\/)|(\/)+/g;

    return url.replace(removeDoubleSlash, '$1$2');
  }

  convertToClientUrl(rawUrl: string, baseHref = ''): string {
    if (rawUrl.startsWith('~')) {
      rawUrl = rawUrl.substring(2);
    }
    baseHref = baseHref || '';

    const url = `${baseHref}/${rawUrl}`;
    return this.removeDoubleSlash(url);
  }

  handleMessage(event: MessageEvent): void {
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (event.data.msg) {
      case 'iframe click':
        this.handleClickHref(event.data);
        break;
      case 'href':
        this.handleClickHref(event.data);
        break;
      case 'redirect':
        this.handleRedirect(event.data);
        break;
      case 'navigate':
        this.router.navigate([event.data.route], { queryParams: event.data.queryParams });
        break;
      case 'open_new_modal':
        // this.openModalByParams(ReportProblemModalComponent, hid, event.data?.plan);
        break;
      case InteractionEvent.TOAST_SHOW:
        // eslint-disable-next-line no-case-declarations
        const payload = event.data;
        this.toastService.toast(new Toast({ content: payload.content, type: payload.type }));
        break;
      case InteractionEvent.ERROR_MODAL_OPEN:
        // this.errorService.showToastWithDetails({ error: event.data.details, toastText: 'Something wrong, please...' });
        break;
      default:
        break;
    }
  }

  handleWindowBlur(): void {
    if (this.dom.activeElement === this.iframeRef()?.nativeElement) {
      this.hostEl.nativeElement.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      this.hostEl.nativeElement.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
    }
  }

  private openModalByParams(modalComponent) {
    /*     if (hid) {
      this.computersFacade
        .getByHid(hid)
        .pipe(filter(Boolean), take(1))
        .subscribe((computer) => {
          const openRefModal = this.modal.openRef(modalComponent);
          openRefModal.componentInstance.computer = computer;
          if (plan) openRefModal.componentInstance.plan = plan;
          openRefModal.result.finally(noop);
        });
      this.computersFacade.loadComputerByHid({ hid });
    } else {
      this.toastService.error('test''));
    }
 */
  }

  handleClickHref(data: { url: string; target?: string }): void {
    if (!data.url || data.url.startsWith('javascript:') || data.target == '_blank') {
      return;
    }

    if (!data.url.startsWith('http') || !this.iframeSrc()) return;

    const url = new URL(data.url);

    const iframeUrl = new URL(this.iframeSrc());
    // update only query parameters
    if (url.origin === iframeUrl.origin && url.pathname === iframeUrl.pathname) {
      if (url.search !== iframeUrl.search) {
        url.searchParams.delete('IFRAME');

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: url.searchParams
        });

        this.iframeSrc.set(this.appendIframeParameter(url.href));
        setTimeout(() => {
          this.iframeWindow() && (this.iframeWindow().location.href = this.iframeSrc());
        });
      }

      return;
    }

    const prefixAdmin = this.convertToClientUrl('/Admin/', this.appBaseHref).toLowerCase();
    const prefixAP = this.convertToClientUrl('/AP/', this.appBaseHref).toLowerCase();
    // redirect to other SPA page
    if (url.pathname.toLowerCase().startsWith(prefixAdmin)) {
      const navigateUrl = url.pathname.replace(new RegExp(prefixAdmin, 'i'), '/AP/') + url.search;

      this.router.navigateByUrl(navigateUrl);
      // redirect to other SPA page from pages /Admin/ by AP link
    } else if (url.pathname.toLowerCase().startsWith(prefixAP)) {
      const navigateUrl = url.pathname.replace(new RegExp(prefixAP, 'i'), '/AP/') + url.search;
      this.router.navigateByUrl(navigateUrl);
    }
  }

  /*
   * Generic redirect
   */
  handleRedirect(data: { msg?: string; redirectUrl: string; sameHost?: boolean; statusCode?: number; pushState?: boolean }): void {
    if (data.sameHost) {
      const url = new URL(data.redirectUrl, window.location.origin);
      if (url.search) {
        if (data.pushState) {
          this.dom.location.assign(data.redirectUrl);
        } else {
          this.dom.location.replace(data.redirectUrl);
        }
      } else {
        const localUrl = this.removeDoubleSlash(data.redirectUrl.replace(this.appBaseHref, '/'));
        this.router.navigate([localUrl]);
      }
    } else if (data.pushState) {
      this.dom.location.assign(data.redirectUrl);
    } else {
      this.dom.location.replace(data.redirectUrl);
    }
  }

  private readonly LOGIN_PAGES = ['Default.aspx', 'PartnerLogin.aspx', 'ForgotPass.aspx'];

  private updateHeaderFooterVisibility(pathname: string): void {
    this.pageLayoutSettings.isLoginPage.set(this.LOGIN_PAGES.some((page) => pathname.includes(page)));
  }

  private updateURL(urlParam: string): void {
    if (!urlParam || urlParam === 'about:blank') {
      return;
    }

    const url = new URL(urlParam);
    this.updateHeaderFooterVisibility(url.pathname);
    const prefixAdmin = this.convertToClientUrl('/Admin/', this.appBaseHref).toLowerCase();
    const prefixAP = this.convertToClientUrl('/AP/', this.appBaseHref).toLowerCase();
    // redirect to other SPA page
    if (url.pathname.toLowerCase().startsWith(prefixAdmin)) {
      const navigateUrl = url.pathname.replace(new RegExp(prefixAdmin, 'i'), '/AP/') + url.search;

      this.router.navigateByUrl(navigateUrl);
      // this.location.go(navigateUrl);
      // redirect to other SPA page from pages /Admin/ by AP link
    } else if (url.pathname.toLowerCase().startsWith(prefixAP)) {
      const navigateUrl = url.pathname.replace(new RegExp(prefixAP, 'i'), '/AP/') + url.search;
      this.router.navigateByUrl(navigateUrl);
    }
  }

  handleLoadIframe(event: Event): void {
    this.loading.set(false);
    this.handleAuth(event?.target as HTMLIFrameElement);
    this.updateURL((event?.target as HTMLIFrameElement).contentWindow?.location.href || '');
    this.watchIframeNavigation();
  }

  private watchIframeNavigation(): void {
    try {
      this.iframeWindow()?.addEventListener('pagehide', () => this.zone.run(() => this.loading.set(true)), { once: true });
    } catch {
      // Cross-origin iframe — nothing to observe, same as handleAuth()
    }
  }

  private readonly authFlowPages = ['PartnerLogin.aspx', 'ForgotPass.aspx', 'NewPassword.aspx'];

  private isAuthFlowPage(url: string): boolean {
    return this.authFlowPages.some((page) => url.includes(page));
  }

  handleAuth(iframe: HTMLIFrameElement): void {
    try {
      const iframeUrl = iframe.contentWindow?.location?.href;

      if (!iframeUrl || iframeUrl === 'about:blank') {
        return;
      }

      const pathname = new URL(iframeUrl).pathname;

      if (pathname.endsWith('.aspx') && !this.isAuthFlowPage(iframeUrl)) {
        this.auth.checkAuthValue(true);
      } else {
        this.auth.checkAuthValue(false);
      }
    } catch {
      // Cross-origin error means iframe is on different domain — ignore
    }
  }
  /*
  handleResizeIframe(): void {
    this.scrollHeight = this.getContentFreeHeight();
  }

  getContentFreeHeight(): number {
    if (!this.layoutElms.header) {
      return window.innerHeight;
    }

    const fillHeight = this.layoutElms.header.clientHeight;
    return window.innerHeight - fillHeight;
  }

  getFooterVisibleHeight(): number {
    const rect = this.layoutElms.footer.getBoundingClientRect();
    const elemTop = rect.top;

    return Math.max(window.innerHeight - elemTop, 0);
  }
    */
}
